import { useLayoutPartialsContext } from "@circle-react/contexts/layoutPartialsContext";
import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { useMobileDrawerSlideoutEffects } from "@/react/hooks/useMobileDrawerSlideoutEffects";

import { MobileDrawerView } from "./MobileDrawerView";
import type { MobileDrawerControls } from "./types";

const MobileDrawerContext = createContext<MobileDrawerControls | null>(null);
MobileDrawerContext.displayName = "MobileDrawerContext";

export const useMobileDrawer = () => useContext(MobileDrawerContext);

export const MobileDrawerProvider = ({
  children,
  renderLeftSidebar,
}: {
  children: ReactNode;
  renderLeftSidebar?: (() => ReactNode) | null;
}) => {
  const { data } = useLayoutPartialsContext();
  const mobileDrawerPayload = data?.mobile_drawer;
  const [isOpen, setIsOpen] = useState(false);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const open = useCallback(() => {
    if (!mobileDrawerPayload) return;
    setIsOpen(true);
  }, [mobileDrawerPayload]);

  const toggle = useCallback(() => {
    if (!mobileDrawerPayload) return;
    setIsOpen(v => !v);
  }, [mobileDrawerPayload]);

  const isDrawerOpen = Boolean(mobileDrawerPayload) && isOpen;

  useMobileDrawerSlideoutEffects({
    isOpen: isDrawerOpen,
  });

  const value = useMemo<MobileDrawerControls>(
    () => ({
      close,
      isOpen: isDrawerOpen,
      open,
      toggle,
    }),
    [close, isDrawerOpen, open, toggle],
  );

  return (
    <MobileDrawerContext.Provider value={value}>
      {children}
      <MobileDrawerView
        isOpen={isDrawerOpen}
        onClose={close}
        renderLeftSidebar={renderLeftSidebar}
      />
    </MobileDrawerContext.Provider>
  );
};

MobileDrawerProvider.displayName = "MobileDrawerProvider";
